import { useEffect, useRef, useState } from 'react'
import { ChevronDown, LogOut } from 'lucide-react'
import { cn } from '@/shared/utils/cn'
import { useAuth, useLogout, getUserKindLabel } from '@/features/auth'

/** Avatar + dropdown con logout. Click fuera o Escape cierra el menú. */
export function UserCard() {
  const { user } = useAuth()
  const { logout, loading } = useLogout()
  const [open, setOpen] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onClick(event: MouseEvent) {
      if (cardRef.current && !cardRef.current.contains(event.target as Node)) setOpen(false)
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!user) return null

  const firstInitial = user.firstName?.charAt(0) ?? user.username?.charAt(0) ?? '?'
  const lastInitial = user.lastName?.charAt(0) ?? ''
  const initials = (firstInitial + lastInitial).toUpperCase()
  const fullName =
    [user.firstName, user.lastName].filter(Boolean).join(' ').trim() ||
    user.username ||
    user.email
  const kindLabel = getUserKindLabel(user)
  const subLabel =
    user.organization && kindLabel
      ? `${kindLabel}${user.organization.billingType ? ` · ${user.organization.billingType === 'ANNUAL' ? 'Anual' : 'Mensual'}` : ''}`
      : kindLabel || 'Sin organización'

  return (
    <div ref={cardRef} className="relative border-t border-white/5 px-4 py-3.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2.5 rounded-md p-1 text-left transition-colors hover:bg-sidebar-hover"
      >
        <div className="grid h-[34px] w-[34px] place-items-center rounded-full bg-gradient-to-br from-amber-500 to-red-500 text-[13px] font-bold text-white">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold text-white">{fullName}</div>
          <div className="truncate text-[11px] text-ink-subtle">{subLabel}</div>
        </div>
        <ChevronDown
          className={cn('h-4 w-4 text-ink-subtle transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="absolute bottom-full left-3 right-3 mb-1 overflow-hidden rounded-lg border border-white/10 bg-sidebar-active shadow-lg">
          <button
            type="button"
            onClick={logout}
            disabled={loading}
            className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[13px] font-medium text-[#d1d5db] transition-colors hover:bg-sidebar-hover hover:text-white disabled:opacity-50"
          >
            <LogOut className="h-4 w-4" />
            {loading ? 'Cerrando sesión…' : 'Cerrar sesión'}
          </button>
        </div>
      )}
    </div>
  )
}
